import { useMemo } from 'react';
import {
  GetPatientsBalanceResponse,
  GetPatientsBalanceResponse_PatientBalance,
} from '@weave/schema-gen-ts/dist/schemas/payments/messages/commands/commands.pb';
import { PaginatedList } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { useAlert } from '@frontend/design-system';
import { useBulkCollectionsFilterShallowStore } from '../../../../store/bulk-collections-filter-store';
import { Row } from '../../common/mobile-list/row';
import { searchByText } from '../../utils';
import { useBulkCollectionsInfiniteScroll } from './hooks/use-bulk-collections-infinite-scroll';

type MobileListProps = {
  isLoading: boolean;
  collections?: GetPatientsBalanceResponse;
  selected: string[];
  toggleSelection: (patientId: string) => void;
  useNewApi: boolean;
};
export const MobileList = ({ isLoading, collections, selected, toggleSelection, useNewApi }: MobileListProps) => {
  const { t } = useTranslation('payments');
  const alert = useAlert();
  const { balanceType, searchValue } = useBulkCollectionsFilterShallowStore('balanceType', 'searchValue');

  // New API flow - infinite scroll
  const {
    data: infiniteScrollData,
    isLoading: isInfiniteScrollLoading,
    isFetchingNextPage,
    hasNextPage,
    loadMore,
  } = useBulkCollectionsInfiniteScroll({
    enabled: useNewApi ?? false,
  });

  // Legacy client-side filtering
  const filteredCollections = useMemo(() => {
    if (useNewApi) return [];

    let result = collections?.patientBalances ?? [];
    if (searchValue) {
      result = searchByText(result, searchValue);
    }

    if (balanceType.length > 0) {
      result = result.filter((item) => balanceType.includes(item.balanceType));
    }

    return result;
  }, [useNewApi, balanceType, searchValue, collections?.patientBalances]);

  const listData = useNewApi ? infiniteScrollData : filteredCollections;
  const loading = useNewApi ? isInfiniteScrollLoading : isLoading;
  const hasNext = useNewApi ? hasNextPage : false;
  const loadMoreFn = useNewApi ? loadMore : undefined;
  const isFetchingMore = useNewApi ? isFetchingNextPage : false;

  return (
    <PaginatedList
      listData={listData}
      renderListItem={({ index, listItem }) => (
        <Row
          index={index}
          selected={selected}
          data={listItem}
          onClick={(data: GetPatientsBalanceResponse_PatientBalance) => {
            if (!data.phoneNumber) {
              alert.error(t("Request can't be created because no phone number is on file"));
            } else {
              toggleSelection(data.patientId);
            }
          }}
        />
      )}
      hasNextPage={hasNext}
      loadMore={loadMoreFn}
      isLoading={loading}
      isFetchingNextPage={isFetchingMore}
      endOfListText={`🎉 ${t('You have reached the end')}`}
    />
  );
};
